/* eslint-disable filetree-structure/services -- a part of the `playback`
service, imported by path rather than re-exported. The rule allows only
platform extensions beside the folder-named file. */
import type { ApiServices } from "$src/core/contexts/api";
import type {
  ContentDto,
  PlaybackDto,
  SeriesContentDto,
} from "$src/core/models/dto";
import { pickImageUrl } from "$src/core/services/car/catalog/catalog";
import type {
  CarContentType,
  CarPlaybackDescriptor,
} from "$src/core/services/car/types";
import toSourceMimeType from "$src/core/utils/player/to-source-mime-type";

/** Mirrored in `CarFairPlayLicenseHandler.swift` and `ComcastDrmFairPlayContentProtectionIntegration.ts` — change together. */
const extractDrm = (
  drm: unknown,
): Pick<
  CarPlaybackDescriptor,
  | "drmScheme"
  | "drmLicenseUrl"
  | "drmHeaders"
  | "drmCertificateUrl"
  | "drmReleasePid"
> => {
  if (!drm || typeof drm !== "object") return {};

  const value = drm as Record<string, unknown>;

  const fairplay = value.fairplay as Record<string, unknown> | undefined;
  if (fairplay && typeof fairplay.certificateURL === "string") {
    const integrationParameters = value.integrationParameters as
      | Record<string, unknown>
      | undefined;
    return {
      drmScheme: "fairplay",
      drmCertificateUrl: fairplay.certificateURL,
      drmLicenseUrl:
        typeof fairplay.licenseAcquisitionURL === "string"
          ? fairplay.licenseAcquisitionURL
          : undefined,
      drmReleasePid:
        typeof integrationParameters?.releasePid === "string"
          ? integrationParameters.releasePid
          : undefined,
      drmHeaders: integrationParameters?.headers as
        | Record<string, string>
        | undefined,
    };
  }

  const widevine = value.widevine as Record<string, unknown> | undefined;
  if (widevine && typeof widevine.licenseAcquisitionURL === "string") {
    return {
      drmScheme: "widevine",
      drmLicenseUrl: widevine.licenseAcquisitionURL,
      drmHeaders: widevine.headers as Record<string, string> | undefined,
    };
  }

  // Raw connector shape.
  if (typeof value.laurl === "string") {
    return {
      drmScheme: "widevine",
      drmLicenseUrl: value.laurl,
      drmHeaders: value.headers as Record<string, string> | undefined,
    };
  }

  return {};
};

const buildPlayback = (
  request: { id: string; type: CarContentType },
  content: ContentDto,
  playback: PlaybackDto,
): CarPlaybackDescriptor => {
  const primary = playback.sources[0];

  return {
    id: request.id,
    type: request.type,
    title: content.title,
    subtitle: content.subtitle,
    artworkUrl: pickImageUrl(content),
    url: primary.url,
    mimeType: toSourceMimeType(primary.type, primary.url),
    ...extractDrm(primary.drm),
  };
};

/** Furthest-reached episode, or the next if finished: the car surface has no episode drill-down. */
export const pickSeriesEpisodeId = (
  series: SeriesContentDto,
): string | undefined => {
  const episodes =
    series.seasons?.flatMap((season) => season.episodes ?? []) ?? [];

  const lastStartedIndex = episodes.findLastIndex(
    (episode) => (episode.progress ?? 0) > 0,
  );

  if (lastStartedIndex === -1) return episodes[0]?.id;

  const lastStarted = episodes[lastStartedIndex];
  if ((lastStarted.progress ?? 0) < 100) return lastStarted.id;

  return (episodes[lastStartedIndex + 1] ?? lastStarted).id;
};

const fetchPlayableContent = async (
  api: ApiServices,
  type: CarContentType,
  id: string,
): Promise<ContentDto | null> => {
  switch (type) {
    case "movies":
      return api.getMovieContent(id);
    case "episodes":
      return api.getEpisodeContent(id);
    case "linear":
      return api.getLinearContent(id);
    case "series": {
      const series = await api.getSeriesContent(id);
      const episodeId = pickSeriesEpisodeId(series);
      return episodeId ? api.getEpisodeContent(episodeId) : null;
    }
    default:
      return null;
  }
};

/** Keeps the request id even for series → episode: Android correlates the descriptor back by it. */
const resolveCarPlayback = async (
  api: ApiServices,
  request: { id: string; type: CarContentType },
): Promise<CarPlaybackDescriptor | null> => {
  const { id, type } = request;

  const content = await fetchPlayableContent(api, type, id);
  if (!content?.playback?.sources.length) return null;

  return buildPlayback(
    { id, type: type === "series" ? "episodes" : type },
    content,
    content.playback,
  );
};

export default resolveCarPlayback;
